import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { reportError } from '../lib/errors';

export interface OnboardingState {
    welcome_completed_at?: string;
    welcome_skipped?: boolean;
    checklist_dismissed?: boolean;
}

const CACHE_KEY = 'fds_onboarding_state';

/**
 * Reads and writes profiles.onboarding_state.
 *
 * Stored server-side so the first run follows the user to a new device rather
 * than restarting every time they open the app somewhere else. localStorage is
 * a cache only — it lets the first paint decide whether to show the carousel
 * without a round trip, which is the difference between "no flash" and the
 * welcome screen appearing for a second on every load for existing users.
 *
 * Degrades rather than breaks: if the column isn't there yet (migration not
 * applied), it falls back to the local cache and keeps working.
 */
export function useOnboardingState() {
    const [state, setState] = useState<OnboardingState>(() => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            return cached ? JSON.parse(cached) : {};
        } catch {
            return {};
        }
    });
    const [loading, setLoading] = useState(true);
    const [persistable, setPersistable] = useState(true);

    const cache = (next: OnboardingState) => {
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* quota / private mode */ }
    };

    useEffect(() => {
        let active = true;

        const load = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { if (active) setLoading(false); return; }

            const { data, error } = await supabase
                .from('profiles')
                .select('onboarding_state')
                .eq('id', user.id)
                .single();

            if (!active) return;

            if (error) {
                // 42703 = undefined column: migration not applied to this
                // environment yet. Stay on the local cache instead of blocking
                // the dashboard on a column that isn't there.
                if (error.code === '42703') setPersistable(false);
                setLoading(false);
                return;
            }

            const next = (data?.onboarding_state as OnboardingState) ?? {};
            setState(next);
            cache(next);
            setLoading(false);
        };

        load();
        return () => { active = false; };
    }, []);

    /** Merge a partial update, optimistically — first-run flags never warrant a spinner. */
    const patch = useCallback(async (partial: OnboardingState) => {
        setState((prev) => {
            const next = { ...prev, ...partial };
            cache(next);
            return next;
        });

        if (!persistable) return;

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Re-read before writing so two flags set in quick succession from
        // different components don't clobber each other.
        const { data, error: fetchError } = await supabase
            .from('profiles')
            .select('onboarding_state')
            .eq('id', user.id)
            .single();
        if (fetchError) reportError(fetchError, 'use-onboarding-state.profiles');

        const merged = { ...((data?.onboarding_state as OnboardingState) ?? {}), ...partial };
        await supabase.from('profiles').update({ onboarding_state: merged }).eq('id', user.id);
    }, [persistable]);

    return {
        state,
        loading,
        patch,
        /** True once the user has finished or skipped the welcome carousel. */
        welcomeDone: !!state.welcome_completed_at || !!state.welcome_skipped,
    };
}
